import ErrorHelper from '@/exports/error'
import type { IDateTimeSettings, IActivityListItem } from '@/models/interfaces'
import {
  RequestStatus,
  ScheduledPaymentStatus,
  TransferStatus,
  ActivityType
} from '../MTConstants'
import i18n from '@/i18n'
import { DateTime } from 'luxon'
import helper from '@/exports/helper'

export function checkApiError(result: any, source = '', handleError = true) {
  if (!result?.data?.success) {
    if (handleError) {
      ErrorHelper.handleError(new Error(result?.data?.errorMessage), source)
    }
    return true
  }

  return false
}

export function getDateTimeDisplay(
  utcDate: DateTime | string | undefined,
  dateTimeSettings: IDateTimeSettings
) {
  if (utcDate == undefined) {
    return DateTime.now()
      .setZone(dateTimeSettings.timeZoneNameForMomentJs)
      .toFormat(`${dateTimeSettings.shortDateFormat} '@' h:mm A`)
  }

  if (typeof utcDate == 'string') {
    return DateTime.fromISO(utcDate)
      .setZone(dateTimeSettings.timeZoneNameForMomentJs)
      .toFormat(`${dateTimeSettings.shortDateFormat} '@' h:mm A`)
  }

  return utcDate
    .setZone(dateTimeSettings.timeZoneNameForMomentJs)
    .toFormat(`${dateTimeSettings.shortDateFormat} '@' h:mm A`)
}

export function getDateDisplay(
  utcDate: DateTime | string | undefined,
  dateTimeSettings: IDateTimeSettings,
  convertToTimeZone: boolean
) {
  if (utcDate == undefined) {
    if (convertToTimeZone) {
      return DateTime.now()
        .setZone(dateTimeSettings.timeZoneNameForMomentJs)
        .toFormat(dateTimeSettings.shortDateFormat)
    }

    return DateTime.now().toFormat(dateTimeSettings.shortDateFormat)
  }

  if (typeof utcDate == 'string') {
    if (convertToTimeZone) {
      return DateTime.fromISO(utcDate)
        .setZone(dateTimeSettings.timeZoneNameForMomentJs)
        .toFormat(dateTimeSettings.shortDateFormat)
    }

    return DateTime.fromISO(utcDate).toFormat(dateTimeSettings.shortDateFormat)
  }

  if (convertToTimeZone) {
    return utcDate
      .setZone(dateTimeSettings.timeZoneNameForMomentJs)
      .toFormat(dateTimeSettings.shortDateFormat)
  }

  return utcDate.toFormat(dateTimeSettings.shortDateFormat)
}

export function getPaymentStatusString(
  paymentStatus: number,
  paidByUser?: boolean,
  coparentFullName?: string
) {
  switch (paymentStatus) {
    case TransferStatus.PENDING:
      return paidByUser != undefined
        ? paidByUser
          ? i18n.global.t(
              'payments.activityList.paymentActivity.titleToPending',
              { name: coparentFullName }
            )
          : i18n.global.t(
              'payments.activityList.paymentActivity.titleFromPending',
              { name: coparentFullName }
            )
        : i18n.global.t(
            'payments.activityList.paymentActivity.details.statusPending'
          )
    case TransferStatus.PROCESSED:
      return paidByUser != undefined
        ? paidByUser
          ? i18n.global.t(
              'payments.activityList.paymentActivity.titleToCompleted',
              { name: coparentFullName }
            )
          : i18n.global.t(
              'payments.activityList.paymentActivity.titleFromCompleted',
              { name: coparentFullName }
            )
        : i18n.global.t(
            'payments.activityList.paymentActivity.details.statusCompleted'
          )
    case TransferStatus.FAILED:
      return paidByUser != undefined
        ? paidByUser
          ? i18n.global.t(
              'payments.activityList.paymentActivity.titleToFailed',
              { name: coparentFullName }
            )
          : i18n.global.t(
              'payments.activityList.paymentActivity.titleFromFailed',
              { name: coparentFullName }
            )
        : i18n.global.t(
            'payments.activityList.paymentActivity.details.statusFailed'
          )
    case TransferStatus.CANCELLED:
      return paidByUser != undefined
        ? paidByUser
          ? i18n.global.t(
              'payments.activityList.paymentActivity.titleToCanceled',
              { name: coparentFullName }
            )
          : i18n.global.t(
              'payments.activityList.paymentActivity.titleFromCanceled',
              { name: coparentFullName }
            )
        : i18n.global.t(
            'payments.activityList.paymentActivity.details.statusCanceled'
          )
    default:
      return ''
  }
}

export function getRequestStatusString(requestStatus: number) {
  switch (requestStatus) {
    case RequestStatus.REQUESTED:
      return i18n.global.t(
        'payments.activityList.requestActivity.details.statusRequested'
      )
    case RequestStatus.ACCEPTED:
      return i18n.global.t(
        'payments.activityList.requestActivity.details.statusAccepted'
      )
    case RequestStatus.DECLINED:
      return i18n.global.t(
        'payments.activityList.requestActivity.details.statusDeclined'
      )
    case RequestStatus.CANCELED:
      return i18n.global.t(
        'payments.activityList.requestActivity.details.statusCanceled'
      )
  }
}

export function getScheduledPaymentStatusString(
  scheduledPaymentStatus: number
) {
  switch (scheduledPaymentStatus) {
    case ScheduledPaymentStatus.ENABLED:
      return i18n.global.t(
        'payments.activityList.scheduledPaymentActivity.details.statusScheduled'
      )
    case ScheduledPaymentStatus.DISABLED:
      return i18n.global.t(
        'payments.activityList.scheduledPaymentActivity.details.statusPaused'
      )
    case ScheduledPaymentStatus.CANCELED:
    case ScheduledPaymentStatus.SYSTEM_CANCELED_UNVERIFIED_CUSTOMER:
    case ScheduledPaymentStatus.SYSTEM_CANCELED_UNVERIFIED_FUNDINGSOURCE:
    case ScheduledPaymentStatus.SYSTEM_CANCELED_COPARENT_UNVERIFIED:
    case ScheduledPaymentStatus.SYSTEM_CANCELED_COPARENT_FUNDINGSOURCE_UNVERIFIED:
      return i18n.global.t(
        'payments.activityList.scheduledPaymentActivity.details.statusCanceled'
      )
  }
}

export function getSortByDate(
  parent: any,
  child: any,
  childPrecedence: string
) {
  return child
    ? child[childPrecedence]
    : parent.lastUpdatedWhen
      ? parent.lastUpdatedWhen
      : parent.createdWhen
}

export function getRelatedRequestActivityTitle(
  requestedByUser: boolean,
  requestStatus: number,
  coParentFullName: string
) {
  return requestStatus == RequestStatus.DECLINED
    ? requestedByUser
      ? i18n.global.t(
          'payments.activityList.requestActivity.relatedTitleDeclined',
          { name: coParentFullName }
        )
      : i18n.global.t(
          'payments.activityList.requestActivity.relatedTitleDeclinedByYou'
        )
    : requestStatus == RequestStatus.CANCELED
      ? requestedByUser
        ? i18n.global.t(
            'payments.activityList.requestActivity.relatedTitleCanceledByYou'
          )
        : i18n.global.t(
            'payments.activityList.requestActivity.relatedTitleCanceled',
            { name: coParentFullName }
          )
      : ''
}

export function getRelatedScheduledPaymentActivityTitle(
  scheduledByUser: boolean,
  status: number,
  repeats: boolean,
  coParentFullName: string
) {
  switch (status) {
    case ScheduledPaymentStatus.CANCELED:
      return scheduledByUser
        ? i18n.global.t(
            'payments.activityList.scheduledPaymentActivity.relatedTitleCanceledByYou'
          )
        : i18n.global.t(
            'payments.activityList.scheduledPaymentActivity.relatedTitleCanceled',
            { name: coParentFullName }
          )
    case ScheduledPaymentStatus.SYSTEM_CANCELED_UNVERIFIED_CUSTOMER:
    case ScheduledPaymentStatus.SYSTEM_CANCELED_UNVERIFIED_FUNDINGSOURCE:
    case ScheduledPaymentStatus.SYSTEM_CANCELED_COPARENT_UNVERIFIED:
    case ScheduledPaymentStatus.SYSTEM_CANCELED_COPARENT_FUNDINGSOURCE_UNVERIFIED:
    case ScheduledPaymentStatus.SYSTEM_CANCELED_FAILED_PAYMENT:
      return i18n.global.t(
        'payments.activityList.scheduledPaymentActivity.relatedTitleSystemCanceled'
      )
    default:
      return ''
  }
}

export function getRelatedScheduledPaymentActivityDetails(
  scheduledByUser: boolean,
  status: number
) {
  switch (status) {
    case ScheduledPaymentStatus.ENABLED:
    case ScheduledPaymentStatus.CANCELED:
      return ''
    case ScheduledPaymentStatus.SYSTEM_CANCELED_UNVERIFIED_CUSTOMER:
      return scheduledByUser
        ? i18n.global.t(
            'payments.activityList.scheduledPaymentActivity.details.relatedDetailsAccount'
          )
        : i18n.global.t(
            'payments.activityList.scheduledPaymentActivity.details.relatedDetailsAccountCoparent'
          )
    case ScheduledPaymentStatus.SYSTEM_CANCELED_COPARENT_UNVERIFIED:
      return scheduledByUser
        ? i18n.global.t(
            'payments.activityList.scheduledPaymentActivity.details.relatedDetailsAccountCoparent'
          )
        : i18n.global.t(
            'payments.activityList.scheduledPaymentActivity.details.relatedDetailsAccount'
          )
    case ScheduledPaymentStatus.SYSTEM_CANCELED_UNVERIFIED_FUNDINGSOURCE:
      return scheduledByUser
        ? i18n.global.t(
            'payments.activityList.scheduledPaymentActivity.details.relatedDetailsBankAccount'
          )
        : i18n.global.t(
            'payments.activityList.scheduledPaymentActivity.details.relatedDetailsBankAccountCoparent'
          )
    case ScheduledPaymentStatus.SYSTEM_CANCELED_COPARENT_FUNDINGSOURCE_UNVERIFIED:
      return scheduledByUser
        ? i18n.global.t(
            'payments.activityList.scheduledPaymentActivity.details.relatedDetailsBankAccountCoparent'
          )
        : i18n.global.t(
            'payments.activityList.scheduledPaymentActivity.details.relatedDetailsBankAccount'
          )
    case ScheduledPaymentStatus.SYSTEM_CANCELED_FAILED_PAYMENT:
      return i18n.global.t(
        'payments.activityList.scheduledPaymentActivity.details.relatedDetailsFailedPayment'
      )
    default:
      return ''
  }
}

export function getPaymentTitle(
  paidByUser: boolean,
  paymentStatus: number,
  coParentFullName: string
) {
  if (paymentStatus == TransferStatus.CANCELLED) {
    return i18n.global.t('payments.activityList.paymentActivity.titleCanceled')
  }
  return getPaymentStatusString(paymentStatus, paidByUser, coParentFullName)
}

export function getScheduledPaymentTitle(
  paidByUser: boolean,
  repeat: boolean,
  coParentFullName: string
) {
  if (repeat) {
    return paidByUser
      ? i18n.global.t(
          'payments.activityList.scheduledPaymentActivity.titleToRecurring',
          { name: coParentFullName }
        )
      : i18n.global.t(
          'payments.activityList.scheduledPaymentActivity.titleFromRecurring',
          { name: coParentFullName }
        )
  }

  return paidByUser
    ? i18n.global.t(
        'payments.activityList.scheduledPaymentActivity.titleToScheduled',
        { name: coParentFullName }
      )
    : i18n.global.t(
        'payments.activityList.scheduledPaymentActivity.titleFromScheduled',
        { name: coParentFullName }
      )
}

export function getScheduledPaymentSubTitle(
  repeat: boolean,
  utcDate: DateTime | string,
  dateTimeSettings: IDateTimeSettings
) {
  if (typeof utcDate == 'string') {
    utcDate = DateTime.fromISO(utcDate, { zone: 'utc' })
  }

  utcDate = utcDate.setZone('utc')

  const scheduledWhenDisplay = utcDate.toFormat(
    dateTimeSettings.shortDateFormat
  )
  const scheduledWhenDayOfMonth = helper.getOrdinalDayOfMonth(utcDate)

  return repeat
    ? i18n.global.t(
        'payments.activityList.scheduledPaymentActivity.subtitleRepeats',
        { day: scheduledWhenDayOfMonth, date: scheduledWhenDisplay }
      )
    : i18n.global.t(
        'payments.activityList.scheduledPaymentActivity.subtitleScheduled',
        { date: scheduledWhenDisplay }
      )
}

export function getBasicRequest(
  request: any,
  requestedByUser: boolean,
  userInitials: string,
  coParentInitials: string,
  coParentFullName: string,
  dateTimeSettings: IDateTimeSettings
) {
  const title = requestedByUser
    ? i18n.global.t('payments.activityList.requestActivity.titleTo', {
        name: coParentFullName
      })
    : i18n.global.t('payments.activityList.requestActivity.titleFrom', {
        name: coParentFullName
      })

  const SendingInitials = requestedByUser ? userInitials : coParentInitials

  return {
    id: request.id,
    title: title,
    relatedActivityTitle: getRelatedRequestActivityTitle(
      requestedByUser,
      request?.status || 0,
      coParentFullName
    ),
    amount: request.amount,
    description: request.description,
    descriptionInitials: SendingInitials,
    viewedWhen: request.viewedWhen ?? null,
    createdWhen: request.createdWhen,
    sending: requestedByUser,
    type: ActivityType.REQUEST,
    threadId: request?.threadId,
    threadSubject: request?.threadSubject
  }
}

export function formatMoney(amount: string | number | undefined) {
  if (!amount) return ''
  if (typeof amount == 'string') {
    return Number.parseFloat(amount).toFixed(2)
  } else {
    return amount
  }
}

// to cast payments, requests, and scheduled payments to activity list items with setting the type
export function toActivityItem(activity: any, type: number) {
  // todo lulu add parameter checks
  const activityItem = activity as IActivityListItem
  activityItem.type = type
  return activityItem
}

export function getHasUpcomingPayment(
  scheduledPayment: IActivityListItem | null | undefined
) {
  if (!scheduledPayment) return false

  if (scheduledPayment.status != ScheduledPaymentStatus.ENABLED) {
    return false
  }

  const now = DateTime.now()
  let scheduledWithin48Hours =
    (scheduledPayment.scheduledWhen ?? DateTime.now()) > now &&
    (scheduledPayment.scheduledWhen ?? DateTime.now()) <=
      DateTime.now().plus({ days: 2 })

  if (scheduledWithin48Hours) return true

  if (
    scheduledPayment.repeat &&
    (!scheduledPayment.lastProcessedWhen ||
      (scheduledPayment.lastProcessedWhen?.day ?? DateTime.now().day) !=
        now.day)
  ) {
    const daysInThisMonth = DateTime.now().daysInMonth
    const nextScheduledDayForThisMonth =
      (scheduledPayment.scheduledWhen?.day ?? DateTime.now().day) >
      daysInThisMonth
        ? daysInThisMonth
        : (scheduledPayment.scheduledWhen?.day ?? DateTime.now().day)
    const nextScheduledDay = now.set({ day: nextScheduledDayForThisMonth })

    scheduledWithin48Hours =
      nextScheduledDay >= (scheduledPayment.scheduledWhen ?? DateTime.now()) &&
      nextScheduledDay > now &&
      nextScheduledDay <= now.plus({ days: 2 })

    if (scheduledWithin48Hours) return true
  }

  return false
}
